import { Link, useLocalSearchParams } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';

import {
  Button,
  ImageAssetWithAltText,
  YStack,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';
import { NutritionalInformation } from '@fhs/ui/src/components/nutritional-information';
import { SodiumDisclaimer } from '@fhs/ui/src/components/nutritional-information/sodium-disclaimer';
import { LaunchDarklyFlag, useFlag } from '@fhs-legacy/frontend/src/state/launchdarkly';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { useStore } from '@fhs-legacy/frontend/src/state/store/hooks';

import { useComboSelectionContext } from '../state/combo-selection';
import { useMenuPicker } from '../state/menu-picker';
import { formatCentsToDollars } from '../utils';
import { useComboSelectionEvents } from '../utils/use-combo-selection-events';

import { ComboGuide } from './combo-guide';
import { CustomizationGroup } from './customization-group';
import { PICKER_SELECTION_TYPE } from './guide/constants';
import { PickerSelectionType } from './guide/types';
import {
  ListItemGroup,
  ListItemGroupSection,
  ListItemLinkPressable,
} from './list-item-group-section';
import { MakeComboSection } from './make-combo-section';
import { PickerAspectOptionSelector } from './picker-aspect-option-selector';
import { ExtraInstructions } from './product-detail-extra-instructions';
import { ProductDetailLayout } from './product-detail-layout';
import { ProductNameAndImage } from './product-name-and-image';

const enabledStatesForSodiumDisclaimer = ['PA', 'NY'];

type NutritionalInformationProps = {
  isShowingNutrition: boolean;
  setIsShowingNutrition: (isShowingNutrition: boolean) => void;
  showSodiumDisclaimer: boolean;
};

const NutritionalInformationTile = ({
  isShowingNutrition,
  setIsShowingNutrition,
  showSodiumDisclaimer,
}: NutritionalInformationProps) => {
  const menuPicker = useMenuPicker();

  return (
    <View>
      <ListItemGroup
        items={[
          {
            render: ({ isFirstItem, isLastItem }) => (
              <ListItemLinkPressable
                titleFontWeight="normal"
                minHeightStyle="content"
                isFirstItem={isFirstItem}
                isLastItem={isLastItem}
                title="Nutritional Information"
                onPress={() => setIsShowingNutrition(true)}
              />
            ),
          },
        ]}
      />

      {showSodiumDisclaimer && <SodiumDisclaimer />}

      <NutritionalInformation
        nutritionFacts={{
          title: menuPicker.selectedPickerAspectOption?.item?.displayName ?? '',
          image: menuPicker.selectedPickerAspectOption?.item?.image as ImageAssetWithAltText,
          // @ts-ignore
          facts: menuPicker.selectedPickerAspectOption?.item?.nutrition ?? {},
        }}
        isVisible={isShowingNutrition}
        onClose={() => setIsShowingNutrition(false)}
      />
    </View>
  );
};

type MenuDetailsProps = Omit<NutritionalInformationProps, 'showSodiumDisclaimer'> & {
  handleAddToCart: () => void;
};

export function MenuItemDetails(props: MenuDetailsProps) {
  const mqStyles = useMqStyles();
  const menuPicker = useMenuPicker();
  const { logPickerTypeSelection } = useComboSelectionEvents();
  const {
    setIsEditingPickers,
    isComboEnabled,
    isComboGuideOpen,
    setIsComboGuideOpen,
    selectedPickers,
    shouldAddToCartDirectly,
    guideSteps,
    addOrUpdateSelectedPicker,
    pickerSelectionType,
    setPickerSelectionType,
  } = useComboSelectionContext();

  const handlePickerTypeSelection = useCallback(
    (selectionType: PickerSelectionType) => {
      setPickerSelectionType(selectionType);
      logPickerTypeSelection(selectionType);
    },
    [setPickerSelectionType, logPickerTypeSelection]
  );

  // Auto-selects combo items in case combo is required and slots contains a preSelectedItemId
  useEffect(() => {
    if (guideSteps && !Object.keys(selectedPickers).length) {
      guideSteps.forEach(step => {
        if (step.preSelectedItemId) {
          const itemToPreSelect = step.sections[0].items.find(
            item => item.id === step.preSelectedItemId
          );

          if (itemToPreSelect) {
            addOrUpdateSelectedPicker(step.id, {
              id: itemToPreSelect.id,
              slug: itemToPreSelect.slug,
              displayName: itemToPreSelect.displayName,
              priceCents: itemToPreSelect.priceCents ?? 0,
              requiresCustomization: false,
              image: itemToPreSelect.image as ImageAssetWithAltText,
            });
          }
        }
      });
    }
  }, [menuPicker.isComboRequired, guideSteps, selectedPickers, addOrUpdateSelectedPicker]);

  const extraInstructionsEnabled = useFlag(LaunchDarklyFlag.ENABLE_EXTRA_INSTRUCTIONS_ON_ITEM);
  const enableComboDefaultSelection = useFlag(
    LaunchDarklyFlag.ENABLE_MENU_DETAILS_DEFAULT_COMBO_SELECTION
  );
  const params = useLocalSearchParams<{ slug: string }>();
  const requiresPickAspectOptionSelection =
    menuPicker.query.data?.pickerAspect?.options &&
    menuPicker.query.data?.pickerAspect?.options.length > 1;
  const combo = menuPicker.query.data?.combo;

  const { handleAddToCart } = props;

  const customizationGroups =
    menuPicker.selectedPickerAspectOption?.item?.customizations?.displayGroups ?? [];
  const topLevelCustomizationGroups = customizationGroups.filter(
    customizationGroup => customizationGroup.displayGroup.showOnProductPage
  );

  const hasCustomizations = customizationGroups.length > 0;
  const baseCaloriesDisplay = menuPicker.selectedPickerAspectOption?.item?.baseCalories
    ? `${menuPicker.selectedPickerAspectOption?.item?.baseCalories} Cal`
    : null;

  const hasCustomizationsContent = requiresPickAspectOptionSelection || hasCustomizations;
  const isDesktop = useMqSelect({ $gteDesktop: true }, false);

  const [comboGuideInitialStepIndex, setComboGuideInitialStepIndex] = useState(0);
  const onComboGuideClose = useCallback(() => {
    setIsComboGuideOpen(false);
    setIsEditingPickers(false);
  }, [setIsEditingPickers, setIsComboGuideOpen]);

  const onComboGuideFinish = useCallback(() => {
    onComboGuideClose();

    if (shouldAddToCartDirectly) {
      handleAddToCart();
    }
  }, [onComboGuideClose, shouldAddToCartDirectly, handleAddToCart]);

  const { store } = useStore();
  const showSodiumDisclaimer = Boolean(
    store &&
      store?.physicalAddress?.stateProvinceShort &&
      enabledStatesForSodiumDisclaimer.includes(
        store.physicalAddress.stateProvinceShort.toUpperCase()
      )
  );
  const pickerOptions = useMemo(() => {
    const comboData = menuPicker.query.data?.combo;

    if (!comboData || comboData.isRequired) {
      return null;
    }

    const image = (menuPicker.query.data?.image as ImageAssetWithAltText) ?? null;

    const comboOption = {
      value: PICKER_SELECTION_TYPE.COMBO,
      title: comboData?.comboMealLabel ?? '',
      image: (comboData?.comboImage as ImageAssetWithAltText) ?? null,
      subtitle:
        formatCentsToDollars((comboData?.basePriceCents ?? 0) + (menuPicker.priceCents ?? 0)) ?? '',
      disabled: false,
    };

    const justSubOption = {
      value: PICKER_SELECTION_TYPE.JUST_SUB,
      title: comboData?.singleItemMealLabel ?? '',
      image,
      subtitle: formatCentsToDollars(menuPicker.priceCents ?? 0) ?? '',
      disabled: false,
    };

    // We want to change the order of options depending on the default one
    return enableComboDefaultSelection
      ? [comboOption, justSubOption]
      : [justSubOption, comboOption];
  }, [menuPicker, enableComboDefaultSelection]);

  const { noStoreSelected } = useStoreContext();

  return (
    <ProductDetailLayout
      isStaticDetails={noStoreSelected}
      nameAndImageContent={
        <>
          <ProductNameAndImage
            isStaticDetails={noStoreSelected}
            displayName={menuPicker.displayName}
            description={menuPicker.query.data?.description ?? ''}
            variant={menuPicker.selectedPickerAspectOption?.displayName ?? ''}
            cals={baseCaloriesDisplay ?? ''}
            asHero={isDesktop || (!requiresPickAspectOptionSelection && !noStoreSelected)}
            imageUrl={menuPicker.query.data?.image?.asset?.uri ?? ''}
            hasCustomizationsContent={hasCustomizationsContent && !noStoreSelected}
            showSodiumDisclaimer={showSodiumDisclaimer}
            heroNutritionalInformationTile={
              (noStoreSelected || !hasCustomizationsContent) && (
                <NutritionalInformationTile
                  setIsShowingNutrition={props.setIsShowingNutrition}
                  isShowingNutrition={props.isShowingNutrition}
                  showSodiumDisclaimer={showSodiumDisclaimer}
                />
              )
            }
          />
          {!isDesktop && noStoreSelected && (
            <View
              style={{
                padding: 16,
              }}
            >
              <View
                style={{
                  borderTopColor: tokens.colors.$blackOpacity04,
                  borderTopWidth: 1,
                  paddingTop: 32,
                  paddingBottom: 16,
                }}
              >
                <NutritionalInformationTile
                  setIsShowingNutrition={props.setIsShowingNutrition}
                  isShowingNutrition={props.isShowingNutrition}
                  showSodiumDisclaimer={showSodiumDisclaimer}
                />
              </View>
            </View>
          )}
        </>
      }
      customizationContent={
        noStoreSelected
          ? null
          : hasCustomizationsContent && (
              <YStack style={mqStyles.customizationContent}>
                {pickerOptions && (
                  <ListItemGroupSection heading={menuPicker.query.data?.combo?.selectionLabel}>
                    <PickerAspectOptionSelector
                      value={pickerSelectionType}
                      onChange={handlePickerTypeSelection}
                      options={pickerOptions ?? []}
                    />
                  </ListItemGroupSection>
                )}
                {requiresPickAspectOptionSelection && (
                  <ListItemGroupSection heading={menuPicker.query.data?.pickerAspect?.displayName}>
                    <PickerAspectOptionSelector
                      value={menuPicker.selectedPickerAspectOptionId ?? ''}
                      onChange={selection => {
                        menuPicker.setSelectedPickerAspectOptionId(selection);
                        menuPicker.setDraftCustomizationSelections({});
                      }}
                      options={
                        menuPicker.query.data?.pickerAspect?.options.map(opt => ({
                          value: opt.id,
                          title: opt.displayName,
                          subtitle: opt.description,
                          image: opt.image as ImageAssetWithAltText,
                          disabled: !opt?.item?.isAvailable,
                          price: null,
                        })) ?? []
                      }
                    />
                  </ListItemGroupSection>
                )}
                {hasCustomizations &&
                  topLevelCustomizationGroups.map((customizationGroup, index) => {
                    const isFirstCustomizationGroup = index === 0;

                    return (
                      <CustomizationGroup
                        customizationSelections={menuPicker.resolvedCustomizationSelections}
                        setCustomizationSelections={newSelections => {
                          menuPicker.setUserCustomizationSelections(newSelections);
                          menuPicker.setDraftCustomizationSelections(newSelections);
                        }}
                        key={customizationGroup.displayGroup.id ?? index}
                        customizationGroup={customizationGroup}
                        footer={
                          isFirstCustomizationGroup &&
                          topLevelCustomizationGroups.length < customizationGroups.length && (
                            <Link
                              href={{
                                pathname: '/v2/menu/[slug]/customize',
                                params: {
                                  slug: params.slug,
                                },
                              }}
                              asChild
                            >
                              <Button type="outline" size="md">
                                <Button.Text>Customize Ingredients</Button.Text>
                              </Button>
                            </Link>
                          )
                        }
                      />
                    );
                  })}

                {combo && isComboEnabled && (
                  <MakeComboSection
                    combo={combo}
                    required={menuPicker.isComboRequired}
                    enabled={isComboEnabled}
                    onEdit={(stepIndex: number) => {
                      setComboGuideInitialStepIndex(stepIndex);
                      setIsComboGuideOpen(true);
                    }}
                  />
                )}
                {isComboGuideOpen && (
                  <ComboGuide
                    steps={guideSteps}
                    onClose={onComboGuideClose}
                    onFinish={onComboGuideFinish}
                    initialStepIndex={comboGuideInitialStepIndex}
                  />
                )}

                {extraInstructionsEnabled && (
                  <ExtraInstructions
                    orderInstructions={menuPicker.instructions}
                    onInstructionsChange={menuPicker.setInstructions}
                  />
                )}

                <View style={mqStyles.divider} />

                <NutritionalInformationTile
                  setIsShowingNutrition={props.setIsShowingNutrition}
                  isShowingNutrition={props.isShowingNutrition}
                  showSodiumDisclaimer={showSodiumDisclaimer}
                />
              </YStack>
            )
      }
    />
  );
}

const useMqStyles = createMqStyles({
  customizationContent: {
    $base: {
      paddingHorizontal: 16,
      gap: 24,
    },
  },
  divider: {
    $base: {
      height: 1,
      backgroundColor: tokens.colors.$blackOpacity04,
    },
  },
});
